import { Injectable, Injector } from '@angular/core';
import { Observable, BehaviorSubject, tap } from 'rxjs';
import { UserModel } from '../models/user.model';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { UserCredentials, UserRegistration } from 'src/app/core/interfaces/user-credentials.interface';
import { AuthSyncService } from 'src/app/core/services/async-auth.service';
import { JwtService } from 'src/app/core/services/jwt.service';
import { RoundingSettingsService } from 'src/app/modules/settings/services/rounding-settings.service';
import { DropdownInputsSignalService } from 'src/app/core/services/dropdown-inputs-signal.service';
import { RecentScreensService } from 'src/app/core/services/recent-screens.service';
import { UserExperienceLevelService } from 'src/app/shared/services/user-experience-level.service';
import { SmartWizardPreferencesService } from 'src/app/shared/services/smart-wizard-preferences.service';
import { WIZARD_PROGRESS_STORAGE_KEYS } from 'src/app/shared/constants/wizard-progress-storage-keys';
import { GlobalShortcutService } from 'src/app/core/services/global-shortcut.service';

export type UserType = UserModel | undefined;

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private API_USERS_URL = `${environment.apiBaseUrl}/Authentication`;

  private static readonly wizardProgressKeySet = new Set(WIZARD_PROGRESS_STORAGE_KEYS);

  currentUser$: Observable<UserType>;
  isLoading$: Observable<boolean>;
  currentUserSubject: BehaviorSubject<UserType>;
  isLoadingSubject: BehaviorSubject<boolean>;
  user: UserModel;

  get currentUserValue(): UserType {
    return this.currentUserSubject.value;
  }

  set currentUserValue(user: UserType) {
    this.currentUserSubject.next(user);
  }

  constructor(
    private router: Router,
    private http: HttpClient,
    private authAsyncService: AuthSyncService,
    private jwtService: JwtService,
    private roundingSettingsService: RoundingSettingsService,
    private signalService: DropdownInputsSignalService,
    private recentScreensService: RecentScreensService,
    private injector: Injector
  ) {
    this.isLoadingSubject = new BehaviorSubject<boolean>(false);
    this.isLoading$ = this.isLoadingSubject.asObservable();
  }

  login(data: UserCredentials) {
    return this.http.post(
      `${environment.apiBaseUrl}/Authentication/SignIn`,
      data
    );
  }

  refreshToken(): Observable<any> {
    return this.http.post(
      `${environment.apiBaseUrl}/Authentication/Refresh-Token`,
      {
        accessToken: this.getAccessToken(),
        refreshToken: this.getRefreshToken(),
      }
    );
  }

  logout() {
    // Clear rounding settings before clearing session storage
    this.roundingSettingsService.clearRoundingSettingsFromStorage();
    sessionStorage.clear();
    const currentLanguage = localStorage.getItem("current language");

    // Preserve product sync dates
    const syncDates = Object.entries(localStorage)
      .filter(([key]) => key.startsWith('productLastSyncDate_'))
      .reduce((acc, [key, value]) => {
        acc[key] = value;
        return acc;
      }, {} as Record<string, string>);

    // Preserve branch selection preferences (user-specific)
    const branchPreferences = Object.entries(localStorage)
      .filter(([key]) => 
        key.startsWith('branch_selection_dont_show_again_user_') || 
        key.startsWith('selected_branch_id_user_')
      )
      .reduce((acc, [key, value]) => {
        acc[key] = value;
        return acc;
      }, {} as Record<string, string>);

    // Preserve recent screens per user (survives logout so same user gets history back)
    const recentScreens = Object.entries(localStorage)
      .filter(([key]) => key.startsWith('recent_screens_user_'))
      .reduce((acc, [key, value]) => {
        acc[key] = value;
        return acc;
      }, {} as Record<string, string>);
    // Preserve Smart Onboarding (stage1, stage4, legacy experience keys)
    const onboardingPrefs = Object.entries(localStorage)
      .filter(([key]) =>
        key.startsWith(UserExperienceLevelService.getStorageKeyPrefix()) ||
        key.includes('_stage4_done') ||
        key.startsWith(UserExperienceLevelService.getLegacyStorageKeyPrefix())
      )
      .reduce((acc, [key, value]) => {
        acc[key] = value;
        return acc;
      }, {} as Record<string, string>);

    const wizardProgressSmartWizardAndJourney = Object.entries(localStorage)
      .filter(([key]) =>
        AuthService.wizardProgressKeySet.has(key) ||
        key.startsWith('smart_wizard_prefs') ||
        key === 'financial_year_onboarding_dismissed' ||
        key.startsWith('mandatory_onboarding_journey_')
      )
      .reduce((acc, [key, value]) => {
        acc[key] = value;
        return acc;
      }, {} as Record<string, string>);

    // Clear all localStorage
    localStorage.clear();
    
    // Restore preserved values
    localStorage.setItem("current language", currentLanguage ?? "ar");
    Object.entries(syncDates).forEach(([key, value]) => {
      localStorage.setItem(key, value);
    });
    Object.entries(branchPreferences).forEach(([key, value]) => {
      localStorage.setItem(key, value);
    });
    Object.entries(recentScreens).forEach(([key, value]) => {
      localStorage.setItem(key, value);
    });
    Object.entries(onboardingPrefs).forEach(([key, value]: [string, string]) => {
      localStorage.setItem(key, value);
    });
    Object.entries(wizardProgressSmartWizardAndJourney).forEach(([key, value]) => {
      localStorage.setItem(key, value);
    });

    this.authAsyncService.logoutAllTabs();

    try {
      this.injector.get(GlobalShortcutService).clearForSessionEnd();
    } catch {
      /* lazy DI */
    }

    setTimeout(() => {
      this.router.navigate(['/auth/login'], { queryParams: { doRedirect: false } });
    }, 10);
  }

  getUserProfile() {
    return this.http.get(`${environment.apiBaseUrl}/User/Profile`);
  }

  getProfile() {
    // decode token to get user object
    const token = this.getAccessToken();
    if (!token) {
      return new Observable<UserType>((observer) => {
        observer.next(undefined);
        observer.complete();
      });
    }
    const user = this.jwtService.decodeToken(token);
    return user as any;
  }

  forgotPassword(userIdentifier: string) {
    return this.http.post(`${this.API_USERS_URL}/ForgetPassword`, {
      userIdentifier: userIdentifier,
    });
  }

  changePassword(input: {
    userIdentifier: string,
    CurrentPassword: string,
    ConfirmNewPassword: string,
    NewPassword: string
  }): Observable<any> {
    return this.http.post(`${this.API_USERS_URL + '/ChangePassword'}`, input);
  }

  optVerify(email: string, code: string) {
    return this.http.post(`${this.API_USERS_URL}/confirm-code`, {
      email,
      code,
    });
  }

  codeVerify(userIdentifier: string, code: number) {
    return this.http.post(`${this.API_USERS_URL}/ConfirmEmail`, {
      userIdentifier: userIdentifier,
      code: code,
    });
  }

  resetPassword(
    userIdentifier: string,
    password: string,
    confirmPassword: string
  ) {
    return this.http.post(`${this.API_USERS_URL}/ResetPassword`, {
      userIdentifier: userIdentifier,
      password: password,
      confirmPassword: confirmPassword,
    });
  }

  setToken(token: string): void {
    sessionStorage.setItem('accessToken', token);
    try {
      const ux = this.injector.get(UserExperienceLevelService);
      ux.syncFromStorageAfterAuthentication();
    } catch {
      /* lazy DI أو تهيئة مبكرة */
    }
    try {
      const prefs = this.injector.get(SmartWizardPreferencesService);
      prefs.refreshWizardProgressBindingsFromStorage();
    } catch {
      /* lazy DI أو تهيئة مبكرة */
    }
    setTimeout(() => {
      try {
        const shortcuts = this.injector.get(GlobalShortcutService);
        shortcuts.loadShortcuts().subscribe({ error: () => undefined });
      } catch {
        /* lazy DI أو تهيئة مبكرة */
      }
    }, 0);
  }

  setRefreshToken(token: string) {
    sessionStorage.setItem('refreshToken', token);
  }

  setTokenExpiration(expireAt: string) {
    sessionStorage.setItem('expireAt', expireAt);
  }

  /** Subscription end date (validUpto) from SignIn/Refresh-Token response. */
  setValidUpto(validUpto: string) {
    if (validUpto) sessionStorage.setItem('validUpto', validUpto);
  }

  getValidUpto(): string | null {
    return sessionStorage.getItem('validUpto');
  }

  isTokenExpired(): boolean {
    const token = this.getAccessToken();
    if (!token) {
      return true;
    }

    const expireAt = sessionStorage.getItem('expireAt');
    if (!expireAt) {
      return true;
    }

    const expireTime = new Date(expireAt).getTime();
    const now = Date.now();

    return now >= expireTime;
  }


  isLoggedIn(): boolean {
    const token = sessionStorage.getItem('accessToken');
    return !!token;
  }

  getAccessToken() {
    return sessionStorage.getItem('accessToken');
  }

  getRefreshToken() {
    return sessionStorage.getItem('refreshToken');
  }

  invalidateUserPremissions = (flag = false) => {
    let userId: string | null = null;
    const token = this.getAccessToken();
    if (token) {
      userId = this.jwtService.getUserId(token);
    }

    if(flag) return userId;

    // Emit an event that PermissionService can listen to
    if (!!userId) {
      this.signalService.triggerReload('invalidatePermissions');
    }
  }

}
